// src/components/SubscriptionStatus.jsx
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  LinearProgress,
  Button,
  Alert,
  Chip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { API_BASE_URL } from "../config/apiKey.js";

export default function SubscriptionStatus() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState(null);
  const [usage, setUsage] = useState(null);
  const [limits, setLimits] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user && token) {
      fetchSubscription();
      
      // Auto-refresh subscription data every 30 seconds
      const interval = setInterval(fetchSubscription, 30000);
      return () => clearInterval(interval);
    }
  }, [user, token]);

  const fetchSubscription = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/payment/subscription`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (!response.ok) {
        console.error("Subscription API error:", response.status, response.statusText);
        setLoading(false);
        return;
      }
      
      const data = await response.json();
      if (data.success && data.subscription) {
        setSubscription(data.subscription);
        setUsage(data.usage || { today: { prompts: 0, images: 0, videos: 0 }, freePromptsUsed: 0 });
        setLimits(data.limits || {});
      } else {
        console.error("Subscription data error:", data);
      }
    } catch (error) {
      console.error("Error fetching subscription:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Paper sx={{ p: 3, bgcolor: "rgba(15, 23, 42, 0.8)", borderRadius: 2 }}>
        <Typography>Loading subscription...</Typography>
      </Paper>
    );
  }

  if (!subscription || !usage || !limits) {
    return null;
  }

  const planName = subscription.plan ? subscription.plan.charAt(0).toUpperCase() + subscription.plan.slice(1) : "Free";
  const isFree = subscription.plan === "free" || !subscription.plan;

  // Calculate usage percentages with safe access
  const promptsUsed = usage?.today?.prompts || 0;
  const imagesUsed = usage?.today?.images || 0;
  const videosUsed = usage?.today?.videos || 0;

  const promptsLimit = limits?.dailyPrompts || limits?.totalPrompts || 0;
  const promptsPercent = promptsLimit
    ? Math.min((promptsUsed / promptsLimit) * 100, 100)
    : 0;

  const imagesLimit = limits?.dailyImages || 0;
  const imagesPercent = imagesLimit ? Math.min((imagesUsed / imagesLimit) * 100, 100) : 0;

  const videosLimit = limits?.dailyVideos || 0;
  const videosPercent = videosLimit ? Math.min((videosUsed / videosLimit) * 100, 100) : 0;

  return (
    <Paper
      sx={{
        p: 3,
        bgcolor: "rgba(15, 23, 42, 0.8)",
        borderRadius: 2,
        border: "1px solid rgba(37, 99, 235, 0.3)",
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6" sx={{ color: "white", fontWeight: 600 }}>
          Subscription Status
        </Typography>
        <Chip
          label={planName}
          color={isFree ? "default" : "primary"}
          sx={{ fontWeight: 600 }}
        />
      </Box>

      {/* Subscription Expiry Warning */}
      {!isFree && subscription.expiresAt && (
        (() => {
          const expiresAt = new Date(subscription.expiresAt);
          const now = new Date();
          const daysUntilExpiry = Math.ceil((expiresAt - now) / (1000 * 60 * 60 * 24));
          const isExpired = subscription.status === "expired" || subscription.isExpired || expiresAt < now;
          
          if (isExpired) {
            return (
              <Alert 
                severity="error" 
                sx={{ mb: 2, bgcolor: "rgba(211, 47, 47, 0.3)", color: "white" }}
                action={
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => navigate("/pricing")}
                    sx={{ bgcolor: "#ef4444", "&:hover": { bgcolor: "#dc2626" } }}
                  >
                    Renew Now
                  </Button>
                }
              >
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Your subscription has expired!
                </Typography>
                <Typography variant="body2">
                  Please renew your subscription to continue using premium features.
                </Typography>
              </Alert>
            );
          } else if (daysUntilExpiry <= 7) {
            return (
              <Alert 
                severity="warning" 
                sx={{ mb: 2, bgcolor: "rgba(245, 158, 11, 0.3)", color: "white" }}
                action={
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => navigate("/pricing")}
                    sx={{ bgcolor: "#f59e0b", "&:hover": { bgcolor: "#d97706" } }}
                  >
                    Renew Now
                  </Button>
                }
              >
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Your subscription expires in {daysUntilExpiry} day{daysUntilExpiry !== 1 ? "s" : ""}!
                </Typography>
                <Typography variant="body2">
                  Renew now to avoid service interruption.
                </Typography>
              </Alert>
            );
          }
          return null;
        })()
      )}

      {isFree && limits.totalPrompts && (
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
            <Typography variant="body2" sx={{ color: "#38bdf8", fontWeight: "bold" }}>
              Total Free Prompts
            </Typography>
            <Typography variant="body2" sx={{ color: "#38bdf8", fontWeight: "bold" }}>
              {usage?.freePromptsUsed || 0} / {limits.totalPrompts}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={Math.min(((usage?.freePromptsUsed || 0) / limits.totalPrompts) * 100, 100)}
            sx={{
              height: 10,
              borderRadius: 1,
              bgcolor: "rgba(56, 189, 248, 0.1)",
              "& .MuiLinearProgress-bar": {
                bgcolor: ((usage?.freePromptsUsed || 0) / limits.totalPrompts) >= 0.9 ? "#ef4444" : "#38bdf8",
                backgroundImage: "linear-gradient(90deg, #38bdf8 0%, #2563eb 100%)",
              },
            }}
          />
          {usage?.freePromptsUsed >= limits.totalPrompts && (
            <Button
              size="small"
              variant="contained"
              fullWidth
              onClick={() => navigate("/pricing")}
              sx={{ 
                mt: 1.5, 
                bgcolor: "#38bdf8", 
                color: "white",
                fontWeight: "bold",
                "&:hover": { bgcolor: "#2563eb" }
              }}
            >
              Limit Reached - Upgrade Now
            </Button>
          )}
        </Box>
      )}

      {/* Prompts Usage */}
      {limits.dailyPrompts !== null && (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
            <Typography variant="body2" sx={{ color: "rgba(226, 232, 240, 0.8)" }}>
              Prompts Today
            </Typography>
            <Typography variant="body2" sx={{ color: "rgba(226, 232, 240, 0.8)" }}>
              {promptsUsed} / {typeof limits.dailyPrompts === "number" ? limits.dailyPrompts : String(limits.dailyPrompts || 0)}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={promptsPercent}
            sx={{
              height: 8,
              borderRadius: 1,
              bgcolor: "rgba(37, 99, 235, 0.2)",
              "& .MuiLinearProgress-bar": {
                bgcolor: promptsPercent >= 90 ? "#ef4444" : "#38bdf8",
              },
            }}
          />
        </Box>
      )}

      {/* Images Usage */}
      {limits.dailyImages !== null && (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
            <Typography variant="body2" sx={{ color: "rgba(226, 232, 240, 0.8)" }}>
              Images Today
            </Typography>
            <Typography variant="body2" sx={{ color: "rgba(226, 232, 240, 0.8)" }}>
              {imagesUsed} / {typeof limits.dailyImages === "number" ? limits.dailyImages : String(limits.dailyImages || 0)}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={imagesPercent}
            sx={{
              height: 8,
              borderRadius: 1,
              bgcolor: "rgba(37, 99, 235, 0.2)",
              "& .MuiLinearProgress-bar": {
                bgcolor: imagesPercent >= 90 ? "#ef4444" : "#38bdf8",
              },
            }}
          />
        </Box>
      )}

      {/* Videos Usage */}
      {limits.dailyVideos !== null && (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
            <Typography variant="body2" sx={{ color: "rgba(226, 232, 240, 0.8)" }}>
              Videos Today
            </Typography>
            <Typography variant="body2" sx={{ color: "rgba(226, 232, 240, 0.8)" }}>
              {videosUsed} / {limits.dailyVideos === null ? "∞" : (typeof limits.dailyVideos === "number" ? limits.dailyVideos : String(limits.dailyVideos || 0))}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={videosPercent}
            sx={{
              height: 8,
              borderRadius: 1,
              bgcolor: "rgba(37, 99, 235, 0.2)",
              "& .MuiLinearProgress-bar": {
                bgcolor: videosPercent >= 90 ? "#ef4444" : "#38bdf8",
              },
            }}
          />
        </Box>
      )}

      {subscription.expiresAt && (
        <Typography variant="body2" sx={{ color: "rgba(226, 232, 240, 0.6)", mt: 2 }}>
          Expires: {new Date(subscription.expiresAt).toLocaleDateString()}
        </Typography>
      )}

      <Typography variant="body2" sx={{ color: "#22c55e", mt: 1, fontWeight: "bold" }}>
        ✨ Your daily limits reset every 24 hours.
      </Typography>

      <Button
        variant="outlined"
        fullWidth
        onClick={() => navigate("/pricing")}
        sx={{
          mt: 2,
          color: "#38bdf8",
          borderColor: "#38bdf8",
          "&:hover": {
            borderColor: "#38bdf8",
            bgcolor: "rgba(37, 99, 235, 0.2)",
          },
        }}
      >
        {isFree 
          ? "Upgrade Plan" 
          : (subscription.status === "expired" || subscription.isExpired) 
            ? "Renew Subscription" 
            : "Change Plan"}
      </Button>
    </Paper>
  );
}








